/**
 * COMPACT TEMPLATE — Display
 *
 * ⚠️  DO NOT wire usePageDelimitation into this template.
 * The compact layout uses a fixed sidebar + flowing main content.
 * Page splitting is handled by CSS print rules (box-decoration-break: clone),
 * NOT by the React-based usePageDelimitation hook.
 *
 * History: commit dc115433 attempted to add usePageDelimitation here
 * but it broke the sidebar/main split — the sidebar content was pushed
 * to page 2 leaving a huge gap. Reverted to the CSS-based approach.
 *
 * If you need page splitting for compact, first test with 3+ pages of
 * content and verify the sidebar doesn't get displaced.
 */
import { useEffect, useRef, useState } from 'react';

import type { TemplateDisplayProps } from '../types';
import type { CompactParams } from './schema';

import CVContainer from '../../components/CVContainer';
import { usePdfPrintReady } from '../../hooks/usePdfPrintReady';
import { getPageHeightInPx } from '../../utils/page-utils';
import MainContent from './components/MainContent';
import Sidebar from './components/Sidebar';

function getNaturalContentHeight(el: HTMLElement): number {
  const children = Array.from(el.children) as HTMLElement[];
  if (children.length === 0) return el.scrollHeight;
  const parentTop = el.getBoundingClientRect().top;
  let maxBottom = 0;
  for (const child of children) {
    const rect = child.getBoundingClientRect();
    if (rect.height > 0) {
      const childBottom = rect.bottom - parentTop;
      if (childBottom > maxBottom) {
        maxBottom = childBottom;
      }
    }
  }
  return maxBottom || el.scrollHeight;
}

export default function CompactDisplay({
  cv,
  params,
  mode = 'preview',
  onEdit,
}: TemplateDisplayProps<CompactParams>) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isLayoutReady, setIsLayoutReady] = useState(false);
  const [pageCount, setPageCount] = useState(1);

  usePdfPrintReady(mode === 'print', containerRef, isLayoutReady);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const updatePages = () => {
      const aside = container.querySelector('aside');
      const main = container.querySelector('main');

      const asideHeight = aside ? getNaturalContentHeight(aside) : 0;
      const mainHeight = main ? getNaturalContentHeight(main) : 0;
      const naturalHeight = Math.max(asideHeight, mainHeight);

      const pageHeightPx = getPageHeightInPx() || 1123;
      const count = Math.max(1, Math.ceil((naturalHeight - 20) / pageHeightPx));

      setPageCount(count);
      setIsLayoutReady(true);
    };

    updatePages();
    const timer = setTimeout(updatePages, 100);
    const ro = new ResizeObserver(updatePages);

    const aside = container.querySelector('aside');
    const main = container.querySelector('main');
    if (aside) ro.observe(aside);
    if (main) ro.observe(main);

    return () => {
      clearTimeout(timer);
      ro.disconnect();
    };
  }, [cv]);

  return (
    <CVContainer accentColor={params.accentColor} mode={mode}>
      <div
        ref={containerRef}
        className="w-[210mm] flex relative z-[1]"
        style={{
          fontFamily: 'Arial, Helvetica, sans-serif',
          minHeight: `${pageCount * 297}mm`,
          background: `linear-gradient(to right, ${params.accentColor ?? '#00B48A'} 29%, #ffffff 29%)`,
        }}
      >
        <Sidebar cv={cv} mode={mode} onEdit={onEdit} accentColor={params.accentColor} />
        <MainContent cv={cv} mode={mode} onEdit={onEdit} />
      </div>

      {/* Logo CibliJob — fixed in print mode so it repeats on every page */}
      <div
        className={`${mode === 'print' ? 'fixed' : 'absolute'} bottom-2 right-2 pointer-events-none z-[2]`}
      >
        <img src="/logo.svg" alt="Cibli" className="w-12" />
      </div>
    </CVContainer>
  );
}
